import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEpisodes, EpisodeListItem } from '../../hooks/useEpisodes';
import { useAuth } from '../../hooks/useAuth';
import { useSettingsStore } from '../../stores/settingsStore';
import { EpisodeCard } from './EpisodeCard';
import { formatDateForInput } from '../../services/dateFormat';

interface EpisodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { number?: number | null; title: string; date?: string | null; showTitle?: string | null }) => Promise<void>;
  initialData?: { number?: number | null; title: string; date?: string | null; showTitle?: string | null };
  mode: 'create' | 'edit';
}

const EpisodeModal: React.FC<EpisodeModalProps> = ({ isOpen, onClose, onSubmit, initialData, mode }) => {
  const { settings } = useSettingsStore();
  const [number, setNumber] = useState(initialData?.number?.toString() || '');
  const [title, setTitle] = useState(initialData?.title || '');
  const [date, setDate] = useState(initialData?.date || '');
  const [showTitle, setShowTitle] = useState(initialData?.showTitle || '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens with new data
  React.useEffect(() => {
    if (isOpen && initialData) {
      setNumber(initialData.number?.toString() || '');
      setTitle(initialData.title || '');
      setDate(initialData.date || '');
      setShowTitle(initialData.showTitle || '');
    } else if (isOpen && !initialData) {
      setNumber('');
      setTitle('');
      setDate('');
      setShowTitle('');
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Le titre est requis');
      return;
    }

    if (number && parseInt(number) < 0) {
      setError('Le numéro doit être un nombre positif');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit({
        number: number ? parseInt(number) : null,
        title: title.trim(),
        date: date || null,
        showTitle: showTitle.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(mode === 'create' ? 'Erreur lors de la création' : 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      data-testid={mode === 'create' ? 'create-episode-modal' : 'edit-episode-modal'}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-white mb-4">
          {mode === 'create' ? 'Créer un épisode' : 'Modifier l\'épisode'}
        </h2>
        
        {error && (
          <div className="text-red-400 mb-4 text-sm bg-red-400/10 p-3 rounded-lg" data-testid="form-error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2" htmlFor="title">
              Titre *
            </label>
            <input
              data-testid="title-input"
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-300 text-sm mb-2" htmlFor="number">
                Numéro
              </label>
              <input
                data-testid="number-input"
                type="number"
                id="number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                min="1"
              />
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm mb-2" htmlFor="date">
                Date
              </label>
              <input
                data-testid="date-input"
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-300 text-sm mb-2" htmlFor="showTitle">
              Série
            </label>
            <input
              data-testid="show-title-input"
              type="text"
              id="showTitle"
              value={showTitle}
              onChange={(e) => setShowTitle(e.target.value)}
              placeholder={settings.showName}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
            />
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              data-testid="submit-button"
              className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium disabled:opacity-50 transition-colors"
              disabled={isSubmitting}
            >
              {isSubmitting ? (mode === 'create' ? 'Création...' : 'Modification...') : (mode === 'create' ? 'Créer' : 'Enregistrer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EpisodeListProps {
  createModalOpen?: boolean;
}

export const EpisodeList: React.FC<EpisodeListProps> = ({ createModalOpen = false }) => {
  const navigate = useNavigate();
  const { episodes, loading, error, createEpisode, updateEpisode, deleteEpisode } = useEpisodes();
  const { user, logout, loading: authLoading, isAuthenticated } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(createModalOpen);
  const [editingEpisode, setEditingEpisode] = useState<EpisodeListItem | null>(null);
  const [episodeToDelete, setEpisodeToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Redirect to login if not authenticated
  if (!authLoading && !isAuthenticated) {
    window.location.href = '/login';
    return null;
  }

  const handleEdit = (id: string) => {
    navigate(`/episodes/${id}`);
  };

  const handlePresent = (id: string) => {
    document.documentElement.requestFullscreen?.();
    navigate(`/episodes/${id}/present`);
  };

  const handleEditAttributes = (id: string) => {
    const episode = episodes.find(ep => ep.id === id);
    if (episode) {
      setEditingEpisode(episode);
    }
  };

  const handleUpdateEpisode = async (data: { number?: number | null; title: string; date?: string | null; showTitle?: string | null }) => {
    if (!editingEpisode) return;
    await updateEpisode(editingEpisode.id, data);
    setEditingEpisode(null);
  };

  const handleDeleteClick = (id: string) => {
    setEpisodeToDelete(id);
  };

  const confirmDelete = async () => {
    if (!episodeToDelete) return;
    setIsDeleting(true);
    try {
      await deleteEpisode(episodeToDelete);
      setEpisodeToDelete(null);
    } catch {
      // Error handled by hook
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setEpisodeToDelete(null);
  };

  const episodeToDeleteData = episodeToDelete 
    ? episodes.find(ep => ep.id === episodeToDelete) 
    : null;

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div data-testid="loading-state" className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          Chargement...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div data-testid="error-state" className="text-red-400 bg-red-400/10 p-4 rounded-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="episode-list">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900/50 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">
                <span className="text-orange-500">@NICEFOXNET</span>
                <span className="text-gray-500 font-normal ml-2">Épisodes</span>
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              {user && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg">
                    <div className="w-7 h-7 bg-orange-600 rounded-full flex items-center justify-center text-sm font-medium">
                      {user.email?.[0].toUpperCase() || '?'}
                    </div>
                    <span className="text-sm text-gray-300">{user.email}</span>
                  </div>
                  <button
                    onClick={logout}
                    className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    Déconnexion
                  </button>
                </div>
              )}
              <button
                data-testid="create-episode-button"
                onClick={() => setIsCreateModalOpen(true)}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nouvel épisode
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {episodes.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-20">
            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Aucun épisode</h2>
            <p className="text-gray-400 mb-6">Commencez par créer votre premier épisode</p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-medium inline-flex items-center gap-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Créer un épisode
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-400">
                {episodes.length} épisode{episodes.length > 1 ? 's' : ''}
              </p>
            </div>
            
            <div data-testid="episodes-container" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((episode) => (
                <EpisodeCard
                  key={episode.id}
                  episode={episode}
                  onEdit={handleEdit}
                  onPresent={handlePresent}
                  onDelete={handleDeleteClick}
                  onEditAttributes={handleEditAttributes}
                />
              ))}
            </div>
          </>
        )}
      </main>

      {/* Create Modal */}
      <EpisodeModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={createEpisode}
        mode="create"
      />

      {/* Edit Modal */}
      <EpisodeModal
        isOpen={!!editingEpisode}
        onClose={() => setEditingEpisode(null)}
        onSubmit={handleUpdateEpisode}
        initialData={editingEpisode ? {
          number: editingEpisode.number,
          title: editingEpisode.title,
          date: editingEpisode.date ? formatDateForInput(editingEpisode.date) : undefined,
          showTitle: editingEpisode.showTitle,
        } : undefined}
        mode="edit"
      />

      {/* Delete Confirmation Modal */}
      {episodeToDelete && episodeToDeleteData && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <div 
            className="bg-gray-800 rounded-xl p-6 max-w-sm mx-4 border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2 text-center">
              Supprimer l'épisode ?
            </h3>
            <p className="text-gray-400 mb-6 text-center">
              "{episodeToDeleteData.title}" sera supprimé définitivement.
            </p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
